import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Sync as SyncIcon,
  Notifications as NotificationIcon
} from "@mui/icons-material";

const AutomaticScheduling = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <SyncIcon sx={{ fontSize: 40 }} />,
      title: "Calendar Sync",
      description: "Automatically sync with Airbnb, VRBO, and other booking platforms"
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      title: "Smart Scheduling",
      description: "Intelligent scheduling based on check-in/check-out times"
    },
    {
      icon: <NotificationIcon sx={{ fontSize: 40 }} />,
      title: "Instant Notifications",
      description: "Get notified immediately when new bookings are made"
    }
  ];

  const benefits = [
    "Eliminate double bookings and scheduling conflicts",
    "Reduce manual work by up to 80%",
    "Never miss a cleaning appointment again",
    "Optimize cleaner routes and schedules",
    "Real-time updates across all platforms"
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Chip 
          label="⏰ Automatic Scheduling" 
          sx={{ 
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2.5rem", md: "3rem" }
          }}
        >
          Automatic Scheduling
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Never worry about scheduling cleanings again. Our automatic scheduling system 
          syncs with your booking calendars and automatically books cleanings when guests check out.
        </Typography>
      </Box>

      <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            How It Works
          </Typography>
          
          <List sx={{ mb: 4 }}>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckIcon sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText 
                  primary={benefit}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              background: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 4,
              p: 4,
              textAlign: "center"
            }}
          >
            <ScheduleIcon sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="600">
              Seamless Integration
            </Typography>
            <Typography variant="body1" color={theme.palette.text.secondary}>
              Connect once and let FreshSweeper handle all your scheduling needs automatically.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" textAlign="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          Key Features
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ textAlign: "center", height: "100%", background: theme.palette.background.paper }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default AutomaticScheduling;